import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import ReportService from '../../../services/ReportService';
import { PageHeader, Card, Button, Badge, StatTile, Field, Input, Select, Table, Tr, Td, EmptyState } from '../../ui';

const money = (c, cur = 'CAD') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format((c || 0) / 100);
const d10 = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const YEAR = new Date().getFullYear();
const CRA_LINES = {
  itc: '106', collected_adjustment: '104', instalment: '110', rebate: '111', correction: '104',
};

// Preset ranges. `from` is computed back from today; 'all' means no lower bound.
const RANGE_PRESETS = [
  ['7d', '7 days', (t) => addDays(t, -7)],
  ['15d', '15 days', (t) => addDays(t, -15)],
  ['1m', '1 month', (t) => addMonths(t, -1)],
  ['3m', '3 months', (t) => addMonths(t, -3)],
  ['6m', '6 months', (t) => addMonths(t, -6)],
  ['1y', '1 year', (t) => addMonths(t, -12)],
  ['all', 'All time', () => null],
  ['custom', 'Custom', null],
];
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }

const PaymentReports = () => {
  const [tab, setTab] = useState('overview');
  const [from, setFrom] = useState(`${YEAR}-01-01`);
  const [to, setTo] = useState(d10(new Date()));
  const [period, setPeriod] = useState('quarterly');
  const [preset, setPreset] = useState('custom');

  const [summary, setSummary] = useState(null);
  const [gst, setGst] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [adjForm, setAdjForm] = useState(null);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); };
  const params = { from, to };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, l, a] = await Promise.all([
        ReportService.summary(params),
        ReportService.gstHst({ ...params, period }),
        ReportService.ledger(params),
        ReportService.adjustments(params),
      ]);
      setSummary(s.summary);
      setGst(g);
      setLedger(l.rows || []);
      setAdjustments(a.adjustments || []);
    } catch (e) {
      flash(false, e.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, period]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (key, calc) => {
    setPreset(key);
    if (key === 'custom') return;
    const today = new Date();
    setTo(d10(today));
    setFrom(key === 'all' ? '' : d10(calc(today)));
    // auto-pick a sensible reporting period for the GST/HST tab
    if (['7d', '15d', '1m'].includes(key)) setPeriod('monthly');
    else if (['3m', '6m'].includes(key)) setPeriod('quarterly');
    else setPeriod(key === 'all' || key === '1y' ? 'quarterly' : period);
  };
  const onFrom = (v) => { setFrom(v); setPreset('custom'); };
  const onTo = (v) => { setTo(v); setPreset('custom'); };

  const backfill = async () => {
    const ok = await swal({
      title: 'Generate missing invoices?',
      text: 'Creates a Siman Tech invoice (no tax component) for every paid payment that doesn’t have one yet, so the ledger is complete.',
      buttons: ['Cancel', 'Generate'],
    });
    if (!ok) return;
    try {
      const r = await ReportService.backfillInvoices();
      flash(true, `${r.created} invoice(s) generated`);
      load();
    } catch (e) { flash(false, e.error || 'Backfill failed'); }
  };

  const fileReturn = async () => {
    const ok = await swal({
      title: 'Snapshot this return?',
      text: `Freezes the GST/HST figures for ${from} → ${to} as a filed record you can hand to your CPA.`,
      buttons: ['Cancel', 'Snapshot'],
    });
    if (!ok) return;
    try {
      await ReportService.fileReturn({ from, to, period });
      flash(true, 'Return snapshot saved under Filed returns');
    } catch (e) { flash(false, e.error || 'Failed'); }
  };

  const saveAdj = async () => {
    try {
      const body = {
        ...adjForm,
        amount: Math.round(parseFloat(adjForm.amountDollars || 0) * 100),
        cra_line: adjForm.cra_line || CRA_LINES[adjForm.kind],
      };
      if (adjForm.id) await ReportService.updateAdjustment(adjForm.id, body);
      else await ReportService.addAdjustment(body);
      setAdjForm(null);
      flash(true, 'Adjustment saved');
      load();
    } catch (e) { flash(false, e.error || 'Save failed'); }
  };

  const delAdj = async (id) => {
    if (!(await swal({ title: 'Delete this adjustment?', buttons: ['Cancel', 'Delete'], dangerMode: true }))) return;
    try { await ReportService.deleteAdjustment(id); load(); } catch (e) { flash(false, e.error || 'Delete failed'); }
  };

  const TABS = [['overview', 'Overview'], ['gst', 'GST/HST Return'], ['ledger', 'Transaction Ledger'], ['adjust', 'Adjustments']];

  return (
    <>
      <PageHeader
        title="Payment & Tax Reports"
        subtitle="Revenue, GST/HST collected and CRA return figures — manage, snapshot and export for your CPA"
        actions={
          <>
            <Button variant="secondary" icon="fa-file-csv" onClick={() => ReportService.downloadCsv('ledger', params)}>
              Export ledger{from ? '' : ' (all)'}
            </Button>
            <Button variant="secondary" icon="fa-file-csv" onClick={() => ReportService.downloadCsv('gst-hst', { ...params, period })}>
              Export return
            </Button>
            <Button variant="ghost" icon="fa-database" onClick={() => ReportService.downloadCsv('ledger', { from: '', to })}>
              Export ALL records
            </Button>
          </>
        }
      />

      {msg && (
        <div className={`tw-mb-4 tw-rounded-xl tw-px-4 tw-py-2.5 tw-text-sm ${msg.ok ? 'tw-bg-good/10 tw-text-good' : 'tw-bg-live/10 tw-text-live'}`}>{msg.text}</div>
      )}

      <Card className="tw-mb-5">
        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-gap-1.5">
          {RANGE_PRESETS.map(([key, label, calc]) => (
            <button
              key={key}
              onClick={() => applyPreset(key, calc)}
              className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition ${
                preset === key ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted hover:tw-text-heading'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <Field label="From"><Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} placeholder="earliest" /></Field>
          <Field label="To"><Input type="date" value={to} onChange={(e) => onTo(e.target.value)} /></Field>
          <Field label="Reporting period">
            <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </Select>
          </Field>
          <div className="tw-pb-1 tw-text-xs tw-text-content-faint">
            {from ? `${from} → ${to}` : `all records → ${to}`}
          </div>
        </div>
      </Card>

      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`tw-rounded-lg tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition ${tab === k ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted hover:tw-text-heading'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="tw-py-20 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>
      ) : (
        <>
          {tab === 'overview' && summary && (
            <>
              <div className="tw-mb-5 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
                <StatTile icon="fa-sack-dollar" label="Gross sales" value={money(summary.gross_sales)} tone="brand" />
                <StatTile icon="fa-rotate-left" label="Refunds" value={money(summary.refunds_total)} tone="warn" />
                <StatTile icon="fa-scale-balanced" label="Net revenue (excl. tax)" value={money(summary.net_revenue)} tone="good" />
                <StatTile icon="fa-building-columns" label="GST/HST collected" value={money(summary.gst_hst_collected)} tone="info" />
              </div>
              <Card title="At a glance">
                <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-text-sm">
                  <div><div className="tw-text-content-faint">Transactions</div><div className="tw-text-lg tw-font-bold tw-text-heading">{summary.transactions}</div></div>
                  <div><div className="tw-text-content-faint">Refunds issued</div><div className="tw-text-lg tw-font-bold tw-text-heading">{summary.refund_count}</div></div>
                  <div><div className="tw-text-content-faint">QST collected</div><div className="tw-text-lg tw-font-bold tw-text-heading">{money(summary.qst_collected)}</div></div>
                  <div><div className="tw-text-content-faint">PST collected</div><div className="tw-text-lg tw-font-bold tw-text-heading">{money(summary.pst_collected)}</div></div>
                </div>
                {summary.uninvoiced > 0 && (
                  <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-xl tw-bg-warn/10 tw-px-4 tw-py-3 tw-text-sm tw-text-warn">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>{summary.uninvoiced} paid payment(s) in this range have no invoice.</span>
                    <Button size="sm" variant="secondary" onClick={backfill}>Generate missing invoices</Button>
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'gst' && gst && (
            <div className="tw-space-y-4">
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-2">
                <p className="tw-text-sm tw-text-content-muted">
                  CRA GST34 line structure. GST &amp; HST feed the federal return; QST/PST are shown for provincial filing.
                </p>
                <Button size="sm" variant="secondary" icon="fa-lock" onClick={fileReturn}>Snapshot this return</Button>
              </div>
              {gst.periods.filter((p) => p.transactions > 0).length === 0 && (
                <EmptyState icon="fa-receipt" title="No taxable activity in this range" />
              )}
              {gst.periods.filter((p) => p.transactions > 0).map((p) => (
                <Card key={p.label} title={p.label} subtitle={`${p.start} → ${p.end} · ${p.transactions} transaction(s)`}>
                  <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-sm">
                      <tbody>
                        {[
                          ['101', 'Sales and other revenue (excluding GST/HST)', p.line101_sales],
                          ['103', 'GST/HST collected or collectible', p.line103_gst_hst_collected],
                          ['104', 'Adjustments to be added', p.line104_adjustments],
                          ['105', 'Total GST/HST and adjustments', p.line105_total, true],
                          ['106', 'Input tax credits (ITCs)', p.line106_itc],
                          ['107', 'Adjustments to be deducted', p.line107_itc_adjustments],
                          ['108', 'Total ITCs and adjustments', p.line108_total_itc, true],
                          ['109', 'Net tax', p.line109_net_tax, true],
                          ['110', 'Instalments and net tax paid', p.line110_instalments],
                          ['111', 'Rebates', p.line111_rebates],
                          ['113A', 'Balance', p.line113a_balance, true],
                          ['114', 'Refund claimed', p.line114_refund_claimed],
                          ['115', 'Amount owing', p.line115_amount_owing, true],
                        ].map(([line, label, val, bold]) => (
                          <tr key={line} className={`tw-border-b tw-border-line-soft ${bold ? 'tw-font-bold tw-text-heading' : ''}`}>
                            <td className="tw-w-16 tw-py-2 tw-font-mono tw-text-xs tw-text-content-faint">{line}</td>
                            <td className="tw-py-2 tw-pr-4">{label}</td>
                            <td className="tw-py-2 tw-text-right tw-tabular-nums">{money(val)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {Object.keys(p.jurisdictions).length > 0 && (
                    <div className="tw-mt-4">
                      <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">By tax type</div>
                      <Table head={['Tax', 'Taxable base', 'Tax collected']}>
                        {Object.entries(p.jurisdictions).map(([k, v]) => (
                          <Tr key={k}>
                            <Td className="tw-font-semibold tw-text-heading">{k}</Td>
                            <Td>{money(v.base)}</Td>
                            <Td>{money(v.tax)}</Td>
                          </Tr>
                        ))}
                      </Table>
                    </div>
                  )}
                  {(p.provincial_info.qst || p.provincial_info.pst) > 0 && (
                    <p className="tw-mt-3 tw-text-xs tw-text-content-faint">
                      Provincial (file separately): QST {money(p.provincial_info.qst)} · PST {money(p.provincial_info.pst)}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          )}

          {tab === 'ledger' && (
            <Card noPad>
              {ledger.length === 0 ? (
                <EmptyState icon="fa-list" title="No transactions in this range" />
              ) : (
                <Table head={['Date', 'Type', 'Document', 'Customer', 'Subtotal', 'GST', 'HST', 'QST/PST', 'Total', '']}>
                  {ledger.map((r, i) => (
                    <Tr key={r.ref + i}>
                      <Td className="tw-whitespace-nowrap tw-text-content-muted">{d10(r.date)}</Td>
                      <Td><Badge tone={r.kind === 'refund' ? 'warn' : 'good'}>{r.kind}</Badge></Td>
                      <Td className="tw-font-mono tw-text-xs">{r.doc || <span className="tw-text-content-faint">—</span>}{!r.invoiced && <span className="tw-ml-1 tw-text-[10px] tw-text-warn">no inv</span>}</Td>
                      <Td>{r.customer}<div className="tw-text-xs tw-text-content-faint">{r.email}</div></Td>
                      <Td className="tw-tabular-nums">{money(r.subtotal)}</Td>
                      <Td className="tw-tabular-nums">{money(r.gst)}</Td>
                      <Td className="tw-tabular-nums">{money(r.hst)}</Td>
                      <Td className="tw-tabular-nums">{money(r.qst + r.pst)}</Td>
                      <Td className="tw-font-semibold tw-tabular-nums tw-text-heading">{money(r.total)}</Td>
                      <Td />
                    </Tr>
                  ))}
                </Table>
              )}
            </Card>
          )}

          {tab === 'adjust' && (
            <Card
              title="Manual tax adjustments"
              subtitle="ITCs, instalments, rebates and corrections — entered here so Line 109 (net tax) is complete"
              actions={<Button size="sm" icon="fa-plus" onClick={() => setAdjForm({ kind: 'itc', period_start: from, period_end: to, amountDollars: '', description: '', reference: '' })}>Add</Button>}
            >
              {adjustments.length === 0 ? (
                <EmptyState icon="fa-calculator" title="No adjustments" hint="Add input tax credits from your business expenses, instalment payments, or corrections." />
              ) : (
                <Table head={['Period', 'Kind', 'CRA line', 'Amount', 'Description', '']}>
                  {adjustments.map((a) => (
                    <Tr key={a.id}>
                      <Td className="tw-whitespace-nowrap tw-text-content-muted">{a.period_start} → {a.period_end}</Td>
                      <Td>{a.kind.replace(/_/g, ' ')}</Td>
                      <Td className="tw-font-mono tw-text-xs">{a.cra_line || '—'}</Td>
                      <Td className="tw-tabular-nums">{money(a.amount)}</Td>
                      <Td className="tw-text-content-muted">{a.description || '—'}{a.reference ? <span className="tw-text-xs tw-text-content-faint"> · {a.reference}</span> : ''}</Td>
                      <Td className="tw-whitespace-nowrap tw-text-right">
                        <button className="tw-mr-3 tw-text-xs tw-text-brand" onClick={() => setAdjForm({ ...a, amountDollars: (a.amount / 100).toString() })}>Edit</button>
                        <button className="tw-text-xs tw-text-live" onClick={() => delAdj(a.id)}>Delete</button>
                      </Td>
                    </Tr>
                  ))}
                </Table>
              )}
            </Card>
          )}
        </>
      )}

      <Modal show={!!adjForm} onHide={() => setAdjForm(null)} centered>
        <Modal.Header closeButton><Modal.Title className="tw-text-base">{adjForm?.id ? 'Edit' : 'Add'} adjustment</Modal.Title></Modal.Header>
        <Modal.Body>
          {adjForm && (
            <div className="tw-space-y-4">
              <Field label="Type">
                <Select value={adjForm.kind} onChange={(e) => setAdjForm((p) => ({ ...p, kind: e.target.value, cra_line: CRA_LINES[e.target.value] }))}>
                  <option value="itc">Input tax credit (ITC) — line 106</option>
                  <option value="collected_adjustment">Adjustment to tax collected — line 104</option>
                  <option value="instalment">Instalment paid — line 110</option>
                  <option value="rebate">Rebate — line 111</option>
                  <option value="correction">Correction</option>
                </Select>
              </Field>
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <Field label="Period start"><Input type="date" value={adjForm.period_start} onChange={(e) => setAdjForm((p) => ({ ...p, period_start: e.target.value }))} /></Field>
                <Field label="Period end"><Input type="date" value={adjForm.period_end} onChange={(e) => setAdjForm((p) => ({ ...p, period_end: e.target.value }))} /></Field>
              </div>
              <Field label="Amount ($)" hint="Positive adds to that line; use a negative value to subtract.">
                <Input type="number" step="0.01" value={adjForm.amountDollars} onChange={(e) => setAdjForm((p) => ({ ...p, amountDollars: e.target.value }))} />
              </Field>
              <Field label="Description"><Input value={adjForm.description || ''} onChange={(e) => setAdjForm((p) => ({ ...p, description: e.target.value }))} /></Field>
              <Field label="Reference / receipt #"><Input value={adjForm.reference || ''} onChange={(e) => setAdjForm((p) => ({ ...p, reference: e.target.value }))} /></Field>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAdjForm(null)}>Cancel</Button>
          <Button icon="fa-floppy-disk" onClick={saveAdj}>Save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentReports;
